import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { getLevelFromPoints, getLevelTitle } from '../lib/scores'

export default function FriendsPage({ onNavigate }) {
  const { user, profile } = useAuth()
  const [friends, setFriends] = useState([])
  const [pending, setPending] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [loading, setLoading] = useState(true)
  const [linkCopied, setLinkCopied] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [myTotalPoints, setMyTotalPoints] = useState(0)
  const [myAvgScore, setMyAvgScore] = useState(0)
  const [myTopArea, setMyTopArea] = useState(null)

  useEffect(() => {
    if (user) { loadFriends(); loadMyPoints() }
    else setLoading(false)
  }, [user])

  async function loadMyPoints() {
    const { data } = await supabase.from('checkins').select('overall_score').eq('user_id', user.id)
    if (data && data.length > 0) {
      const total = data.reduce((sum, c) => sum + (c.overall_score || 0), 0)
      setMyTotalPoints(total)
      setMyAvgScore(Math.round(total / data.length))
    }
    // Top area from manual_tasks
    const { data: tasks } = await supabase.from('manual_tasks')
      .select('area, final_points').eq('user_id', user.id).eq('flagged', false)
    if (tasks && tasks.length > 0) {
      const areaTotals = {}
      tasks.forEach(t => { areaTotals[t.area] = (areaTotals[t.area] || 0) + (t.final_points || 0) })
      const areaLabels = { health: 'Health', mind: 'Mind', career: 'Career', relationships: 'Relationships', faith: 'Faith', purpose: 'Goals' }
      const top = Object.entries(areaTotals).sort((a, b) => b[1] - a[1])[0]?.[0]
      if (top) setMyTopArea(areaLabels[top] || top)
    }
  }

  async function loadFriends() {
    try {
      const { data } = await supabase.from('friendships')
        .select('id, status, requester_id, addressee_id')
        .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
      if (!data || data.length === 0) { setLoading(false); return }

      const friendIds = [...new Set(data.map(f => f.requester_id === user.id ? f.addressee_id : f.requester_id))]
      const { data: profiles } = await supabase.from('profiles').select('id, full_name, streak').in('id', friendIds)

      const pointsMap = {}
      await Promise.all(friendIds.map(async (fid) => {
        const { data: checkins } = await supabase.from('checkins').select('overall_score').eq('user_id', fid)
        if (checkins) pointsMap[fid] = checkins.reduce((sum, c) => sum + (c.overall_score || 0), 0)
      }))

      const profileMap = {}
      if (profiles) profiles.forEach(p => { profileMap[p.id] = p })

      const accepted = data.filter(f => f.status === 'accepted').map(f => {
        const fid = f.requester_id === user.id ? f.addressee_id : f.requester_id
        return { ...(profileMap[fid] || {}), friendshipId: f.id, totalPoints: pointsMap[fid] || 0 }
      }).filter(f => f.id)

      const pendingIn = data.filter(f => f.status === 'pending' && f.addressee_id === user.id).map(f => ({
        ...(profileMap[f.requester_id] || {}), friendshipId: f.id
      })).filter(f => f.id)

      setFriends(accepted)
      setPending(pendingIn)
    } catch (e) {}
    setLoading(false)
  }

  async function searchUsers(q) {
    if (!q.trim()) { setSearchResults([]); return }
    setSearching(true)
    try {
      const { data } = await supabase.from('profiles')
        .select('id, full_name').ilike('full_name', `%${q}%`).neq('id', user.id).limit(8)
      const friendIds = friends.map(f => f.id)
      setSearchResults((data || []).filter(u => !friendIds.includes(u.id)))
    } catch (e) {}
    setSearching(false)
  }

  async function sendRequest(toId) {
    try {
      await supabase.from('friendships').insert({ requester_id: user.id, addressee_id: toId })
      setSearchResults(prev => prev.filter(u => u.id !== toId))
    } catch (e) {}
  }

  async function acceptRequest(friendshipId) {
    await supabase.from('friendships').update({ status: 'accepted' }).eq('id', friendshipId)
    loadFriends()
  }

  async function removeFriend(friendshipId) {
    await supabase.from('friendships').delete().eq('id', friendshipId)
    setFriends(prev => prev.filter(f => f.friendshipId !== friendshipId))
  }

  function copyInviteLink() {
    if (!user) return
    navigator.clipboard.writeText(`https://lifescore-app.com?invite=${user.id}`)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2500)
  }

  const me = { id: user?.id, full_name: profile?.full_name || 'You', streak: profile?.streak || 0, totalPoints: myTotalPoints, isMe: true }
  const leaderboard = [me, ...friends].sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0))
  const safePoints = myTotalPoints || 0
  const { level: myLevel } = getLevelFromPoints(safePoints)
  const myLevelTitle = getLevelTitle(myLevel)

  return (
    <div style={s.page}>
      <div style={s.ambient} />
      <div style={s.header}>
        <div style={s.backBtn} onClick={() => onNavigate('dashboard')}>←</div>
        <div style={s.headerTitle}>Friends</div>
        <div style={s.addToggle} onClick={() => setShowAdd(v => !v)}>{showAdd ? 'Done' : '+ Add'}</div>
      </div>

      <div style={s.main}>
        {loading ? <div style={s.loading}>Loading...</div> : (
          <>
            {/* PLAYER CARD */}
            {(() => {
              const scoreColor = myAvgScore >= 75 ? '#4caf7d' : myAvgScore >= 50 ? '#c9a84c' : myAvgScore > 0 ? '#e05c5c' : '#6b6860'
              const glowColor = myAvgScore >= 75 ? 'rgba(76,175,125,0.4)' : myAvgScore >= 50 ? 'rgba(201,168,76,0.4)' : myAvgScore > 0 ? 'rgba(224,92,92,0.4)' : 'rgba(107,104,96,0.2)'
              const circumference = 2 * Math.PI * 28
              const offset = circumference - (myAvgScore / 100) * circumference
              return (
                <div style={s.playerCard}>
                  <div style={s.playerCardInner}>
                    {/* Score Circle */}
                    <div style={s.scoreCircleWrap}>
                      <svg width="72" height="72" viewBox="0 0 72 72">
                        <circle cx="36" cy="36" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5"/>
                        <circle cx="36" cy="36" r="28" fill="none" stroke={scoreColor}
                          strokeWidth="5" strokeLinecap="round"
                          strokeDasharray={circumference}
                          strokeDashoffset={offset}
                          transform="rotate(-90 36 36)"
                          style={{ filter: `drop-shadow(0 0 6px ${glowColor})`, transition: 'stroke-dashoffset 1s ease' }}
                        />
                      </svg>
                      <div style={s.scoreCircleInner}>
                        <div style={{ ...s.scoreCircleVal, color: scoreColor }}>{myAvgScore || '—'}</div>
                        <div style={s.scoreCircleLabel}>avg</div>
                      </div>
                    </div>

                    {/* Player Info */}
                    <div style={s.playerInfo}>
                      {/* Avatar + Name row */}
                      <div style={s.playerNameRow}>
                        <div style={s.playerCardAvatar}>
                          {profile?.avatar_url
                            ? <img src={profile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: '14px', fontWeight: '700', color: '#000' }}>{profile?.full_name?.[0]?.toUpperCase() || '?'}</span>
                          }
                        </div>
                        <div style={s.playerName}>{profile?.full_name || 'You'}</div>
                      </div>
                      <div style={s.playerLevelBadge}>
                        <span style={s.playerLevelText}>LVL {myLevel}</span>
                        <span style={s.playerLevelTitle}>{myLevelTitle}</span>
                      </div>
                      <div style={s.playerStatsRow}>
                        <div style={s.playerMiniStat}>
                          <span style={s.playerMiniVal}>🔥 {profile?.streak || 0}</span>
                          <span style={s.playerMiniLabel}>streak</span>
                        </div>
                        <div style={s.playerMiniDivider} />
                        <div style={s.playerMiniStat}>
                          <span style={s.playerMiniVal}>{myTotalPoints.toLocaleString()}</span>
                          <span style={s.playerMiniLabel}>pts</span>
                        </div>
                        {myTopArea && (
                          <>
                            <div style={s.playerMiniDivider} />
                            <div style={s.playerMiniStat}>
                              <span style={s.playerMiniVal}>{myTopArea}</span>
                              <span style={s.playerMiniLabel}>top area</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* ADD FRIENDS */}
            {showAdd && (
              <div style={s.addSection}>
                <div style={s.sectionTitle}>Find Friends</div>
                <input style={s.searchInput} placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); searchUsers(e.target.value) }}
                  autoCapitalize="none" autoCorrect="off" />
                {searching && <div style={s.searching}>Searching...</div>}
                {searchResults.length > 0 && (
                  <div style={s.resultsList}>
                    {searchResults.map((u, i) => (
                      <div key={u.id} style={{ ...s.resultRow, borderBottom: i < searchResults.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                        <div style={s.resultAvatar}>{u.full_name?.[0]?.toUpperCase() || '?'}</div>
                        <div style={s.resultName}>{u.full_name}</div>
                        <div style={s.addBtn} onClick={() => sendRequest(u.id)}>Add +</div>
                      </div>
                    ))}
                  </div>
                )}
                {searchQuery.length > 1 && searchResults.length === 0 && !searching && (
                  <div style={s.noResults}>No users found</div>
                )}
                <div style={s.inviteRow}>
                  <div style={s.inviteLabel}>Can't find them? Send an invite link</div>
                  <div style={{ ...s.inviteBtn, background: linkCopied ? 'rgba(76,175,125,0.12)' : 'rgba(201,168,76,0.08)', color: linkCopied ? '#4caf7d' : '#c9a84c', border: `1px solid ${linkCopied ? 'rgba(76,175,125,0.3)' : 'rgba(201,168,76,0.2)'}` }}
                    onClick={copyInviteLink}>
                    {linkCopied ? '✓ Link Copied!' : '🔗 Copy Invite Link'}
                  </div>
                </div>
                {pending.length > 0 && (
                  <>
                    <div style={{ ...s.sectionTitle, marginTop: '20px' }}>Friend Requests</div>
                    <div style={s.card}>
                      {pending.map((f, i) => (
                        <div key={f.id || i} style={{ ...s.resultRow, borderBottom: i < pending.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                          <div style={s.resultAvatar}>{f.full_name?.[0]?.toUpperCase() || '?'}</div>
                          <div style={s.resultName}>{f.full_name}</div>
                          <div style={s.acceptBtn} onClick={() => acceptRequest(f.friendshipId)}>Accept</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* LEADERBOARD */}
            <div style={s.section}>
              <div style={s.sectionTitle}>All-Time Leaderboard 🏆</div>
              {friends.length === 0 ? (
                <div style={s.emptyCard}>
                  <div style={s.emptyText}>Add friends to see how you stack up on the all-time leaderboard!</div>
                  <div style={s.emptyBtn} onClick={() => setShowAdd(true)}>Add Your First Friend →</div>
                </div>
              ) : (
                <div style={s.card}>
                  {leaderboard.map((f, i) => {
                    const { level } = getLevelFromPoints(f.totalPoints || 0)
                    return (
                      <div key={f.id || i} style={{ ...s.leaderRow, borderBottom: i < leaderboard.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none', background: f.isMe ? 'rgba(201,168,76,0.04)' : 'transparent' }}>
                        <div style={s.rank}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}</div>
                        <div style={{ ...s.leaderAvatar, background: f.isMe ? 'linear-gradient(135deg, #c9a84c, #8b6a1a)' : 'rgba(255,255,255,0.1)', color: f.isMe ? '#000' : '#e8e6df' }}>
                          {f.full_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div style={s.leaderInfo}>
                          <div style={s.leaderName}>{f.isMe ? 'You' : f.full_name?.split(' ')[0]}</div>
                          <div style={s.leaderSub}>Level {level} · 🔥 {f.streak || 0} streak</div>
                        </div>
                        <div style={s.leaderPoints}>
                          <div style={s.leaderPtsVal}>{(f.totalPoints || 0).toLocaleString()}</div>
                          <div style={s.leaderPtsLabel}>pts</div>
                        </div>
                        {!f.isMe && (
                          <div style={s.removeX} onClick={() => removeFriend(f.friendshipId)}>✕</div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const s = {
  page: { minHeight: '100vh', background: '#111118', color: '#e8e6df', fontFamily: "'DM Sans', sans-serif", position: 'relative', overflowX: 'hidden' },
  ambient: { position: 'fixed', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '800px', background: 'radial-gradient(ellipse, rgba(201,168,76,0.12) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'max(18px, env(safe-area-inset-top))', paddingBottom: '18px', paddingLeft: '24px', paddingRight: '24px', background: 'rgba(6,6,8,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.12)' },
  backBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', cursor: 'pointer', fontSize: '18px', color: '#c9a84c' },
  headerTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '3px', color: '#e8e6df' },
  addToggle: { fontSize: '13px', color: '#c9a84c', fontWeight: '600', cursor: 'pointer' },
  main: { maxWidth: '420px', margin: '0 auto', padding: '24px 20px 120px', position: 'relative', zIndex: 1 },
  loading: { textAlign: 'center', padding: '60px 0', color: '#6b6860' },
  playerCard: { background: 'linear-gradient(135deg, #1a1a26, #1a1a28)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '20px', padding: '20px', marginBottom: '24px' },
  playerCardInner: { display: 'flex', alignItems: 'center', gap: '20px' },
  scoreCircleWrap: { position: 'relative', width: '72px', height: '72px', flexShrink: 0 },
  scoreCircleInner: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  scoreCircleVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', lineHeight: 1 },
  scoreCircleLabel: { fontSize: '9px', color: '#6b6860', letterSpacing: '1px', textTransform: 'uppercase' },
  playerInfo: { flex: 1 },
  playerNameRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  playerCardAvatar: { width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
  playerName: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '1.5px', color: '#e8e6df', lineHeight: 1 },
  playerLevelBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '6px', padding: '3px 8px', marginBottom: '10px' },
  playerLevelText: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '13px', color: '#c9a84c', letterSpacing: '1px' },
  playerLevelTitle: { fontSize: '11px', color: '#f0cc7a' },
  playerStatsRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  playerMiniStat: { display: 'flex', flexDirection: 'column' },
  playerMiniVal: { fontSize: '13px', color: '#e8e6df', fontWeight: '600' },
  playerMiniLabel: { fontSize: '10px', color: '#6b6860' },
  playerMiniDivider: { width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' },
  addSection: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '20px', marginBottom: '24px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '12px' },
  searchInput: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '12px 16px', color: '#e8e6df', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box', marginBottom: '8px' },
  searching: { fontSize: '12px', color: '#6b6860', padding: '6px 0' },
  resultsList: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden', marginBottom: '12px' },
  resultRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' },
  resultAvatar: { width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', flexShrink: 0 },
  resultName: { flex: 1, fontSize: '14px', color: '#e8e6df' },
  addBtn: { fontSize: '12px', color: '#c9a84c', fontWeight: '700', cursor: 'pointer' },
  noResults: { fontSize: '13px', color: '#6b6860', padding: '6px 0', marginBottom: '8px' },
  inviteRow: { marginTop: '14px' },
  inviteLabel: { fontSize: '12px', color: '#6b6860', marginBottom: '8px' },
  inviteBtn: { padding: '12px', borderRadius: '10px', textAlign: 'center', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  acceptBtn: { fontSize: '12px', color: '#4caf7d', fontWeight: '600', cursor: 'pointer', background: 'rgba(76,175,125,0.1)', padding: '6px 12px', borderRadius: '8px' },
  card: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', overflow: 'hidden' },
  emptyCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '28px 24px', textAlign: 'center' },
  emptyText: { fontSize: '14px', color: '#6b6860', lineHeight: '1.6', marginBottom: '18px' },
  emptyBtn: { display: 'inline-block', padding: '11px 20px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', color: '#c9a84c', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  section: { marginBottom: '24px' },
  leaderRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 16px' },
  rank: { fontSize: '16px', width: '28px', flexShrink: 0, textAlign: 'center' },
  leaderAvatar: { width: '34px', height: '34px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', flexShrink: 0 },
  leaderInfo: { flex: 1 },
  leaderName: { fontSize: '14px', color: '#e8e6df', fontWeight: '500' },
  leaderSub: { fontSize: '11px', color: '#6b6860', marginTop: '2px' },
  leaderPoints: { textAlign: 'right' },
  leaderPtsVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', color: '#c9a84c', lineHeight: 1 },
  leaderPtsLabel: { fontSize: '10px', color: '#6b6860' },
  removeX: { fontSize: '12px', color: '#6b6860', cursor: 'pointer', padding: '4px', marginLeft: '4px' },
}
